/* =========================================
   Cart Drawer v2.1 — Bulletproof Gift Sync
   
   KEY ARCHITECTURE DECISION:
   Gift rules are evaluated in Liquid (server-side).
   After every cart change, we MUST fetch fresh section
   HTML to get the NEW Liquid-evaluated config.
   We NEVER trust the config that was rendered at page load.
   
   FLOW:
   1. Cart changes (add/remove/update)
   2. Section Rendering returns fresh HTML + fresh gift config
   3. Gift sync reads the FRESH config
   4. Compares with actual /cart.js state
   5. Adds/removes gifts as needed
   6. If changed → re-fetch sections again (loop once)
   7. Unlock checkout only when cart matches all rules
========================================= */

"use strict";

/* =========================================
   SECTION: Logger
========================================= */
const CartLogger = {
  _p: '[Cart]',
  info(...a) { console.log(this._p, ...a); },
  warn(...a) { console.warn(this._p, ...a); },
  error(...a) { console.error(this._p, ...a); },
  debug(...a) { if (window.CART_DEBUG) console.log(this._p, '[DEBUG]', ...a); }
};
window.CartLogger = CartLogger;

/* =========================================
   SECTION: CartDrawer
========================================= */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._giftSyncLock = false;
    this._syncAttempts = 0;
    this._MAX_SYNC_ATTEMPTS = 3;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    /* CRITICAL: Lock checkout on ANY cart interaction instantly.
       Uses event delegation — catches clicks on quantity buttons,
       remove buttons, and input changes before AJAX fires. */
    this.addEventListener('click', (e) => {
      if (e.target.closest('cart-remove-button') ||
          e.target.closest('.quantity__button') ||
          e.target.closest('button[name="minus"]') ||
          e.target.closest('button[name="plus"]')) {
        this.setCheckoutLock(true);
        CartLogger.debug('Checkout locked: user interaction detected');
      }
    });

    this.addEventListener('change', (e) => {
      if (e.target.closest('.quantity__input') || e.target.closest('[data-index]')) {
        this.setCheckoutLock(true);
        CartLogger.debug('Checkout locked: quantity input changed');
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.refreshAndOpen();
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.refreshAndOpen();
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    CartLogger.debug('open');

    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);

    setTimeout(() => { this.classList.add('animate', 'active'); });

    this.addEventListener('transitionend', () => {
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusEl = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (container) trapFocus(container, focusEl);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart:opened'));
  }

  close() {
    CartLogger.debug('close');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart:closed'));
  }

  /* =========================================
     SECTION: Section Rendering
  ========================================= */

  /**
   * Fetches fresh section HTML, updates DOM, runs gift sync, opens drawer.
   * This is the MAIN entry point for opening the cart.
   */
  async refreshAndOpen() {
    CartLogger.debug('refreshAndOpen');
    this.setCheckoutLock(true);

    try {
      await this.refreshSections();
      this.open();
      await this.runGiftSync();
    } catch (err) {
      CartLogger.error('refreshAndOpen failed:', err);
      this.open();
      this.setCheckoutLock(false);
    }
  }

  renderContents(parsedState) {
    CartLogger.debug('renderContents', { id: parsedState.id });
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach(section => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (el && parsedState.sections?.[section.id]) {
        el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    this.rebindOverlay();

    setTimeout(async () => {
      this.open();
      await this.runGiftSync();
    });
  }

  async refreshSections() {
    const ids = this.getSectionsToRender().map(s => s.id);
    const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    const sections = await res.json();

    this.getSectionsToRender().forEach(section => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (el && sections[section.id]) {
        el.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      }
    });

    this.rebindOverlay();
  }

  rebindOverlay() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }

  setSummaryAccessibility(el) {
    el.setAttribute('role', 'button');
    el.setAttribute('aria-expanded', 'false');
    if (el.nextElementSibling?.id) el.setAttribute('aria-controls', el.nextElementSibling.id);
    el.addEventListener('click', e => {
      e.currentTarget.setAttribute('aria-expanded',
        !e.currentTarget.closest('details').hasAttribute('open'));
    });
    el.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  /* =========================================
     SECTION: Gift Sync Controller v2 (Bulletproof)
     
     ALGORITHM:
     1. Lock checkout immediately
     2. Fetch FRESH section HTML → get fresh Liquid-evaluated gift config
     3. Fetch /cart.js → get actual cart items
     4. Compare: for each rule, check if gift should be in cart
     5. Add missing gifts / remove orphaned gifts
     6. If any changes were made → fetch sections AGAIN (new Liquid state)
     7. Validate final state matches rules
     8. Unlock checkout ONLY if everything is clean
     
     ABUSE PREVENTION:
     - Max 3 sync attempts to prevent infinite loops
     - Checkout stays locked if sync fails
     - Gift quantities are capped to maxQty
  ========================================= */

  async runGiftSync() {
    if (this._giftSyncLock) {
      CartLogger.debug('Gift sync: skipped (lock active)');
      return;
    }

    this._giftSyncLock = true;
    this._syncAttempts = 0;
    this.setCheckoutLock(true);
    document.dispatchEvent(new CustomEvent('cart:gift-sync-start'));
    CartLogger.debug('=== GIFT SYNC START ===');

    try {
      let isClean = false;

      while (!isClean && this._syncAttempts < this._MAX_SYNC_ATTEMPTS) {
        this._syncAttempts++;
        CartLogger.debug(`Gift sync: attempt ${this._syncAttempts}/${this._MAX_SYNC_ATTEMPTS}`);

        /* Step 1: Fetch config AND cart in PARALLEL (saves ~200-400ms) */
        const [freshConfig, cart] = await Promise.all([
          this.fetchFreshGiftConfig(),
          this.fetchCart()
        ]);

        if (!freshConfig || freshConfig.length === 0) {
          CartLogger.debug('No gift rules configured — sync complete');
          isClean = true;
          break;
        }

        CartLogger.debug('Cart state:', {
          items: cart.item_count,
          total: cart.total_price
        });

        /* Step 2: Build actions list */
        const actions = this.calculateGiftActions(freshConfig, cart);

        if (actions.length === 0) {
          CartLogger.debug('Gift sync: cart is clean, no actions needed');
          isClean = true;
          break;
        }

        /* Step 3: Execute actions in BATCHES (not one-by-one) */
        CartLogger.info(`Gift sync: executing ${actions.length} action(s)`);

        const toAdd = actions.filter(a => a.type === 'add');
        const toRemove = actions.filter(a => a.type === 'remove');
        const toCap = actions.filter(a => a.type === 'cap');

        const promises = [];

        /* Batch all additions into ONE /cart/add.js call */
        if (toAdd.length > 0) {
          const items = toAdd.map(a => ({ id: a.variantId, quantity: a.quantity }));
          promises.push(
            this.cartPost('/cart/add.js', { items })
              .then(() => CartLogger.info('Gift sync: ADDED', items.length, 'gift(s)'))
          );
        }

        /* Batch all removals + caps into ONE /cart/update.js call */
        if (toRemove.length > 0 || toCap.length > 0) {
          const updates = {};
          toRemove.forEach(a => { updates[a.variantId] = 0; });
          toCap.forEach(a => { updates[a.variantId] = a.quantity; });
          promises.push(
            this.cartPost('/cart/update.js', { updates })
              .then(() => CartLogger.info('Gift sync: REMOVED/CAPPED', Object.keys(updates).length, 'gift(s)'))
          );
        }

        /* Execute add + remove/cap in PARALLEL */
        await Promise.all(promises);

        /* Step 4: Refresh sections to reflect changes */
        await this.refreshSections();
      }

      if (!isClean) {
        CartLogger.warn('Gift sync: max attempts reached');
      }

      CartLogger.debug('=== GIFT SYNC END ===');
    } catch (err) {
      CartLogger.error('Gift sync failed:', err);
    } finally {
      this._giftSyncLock = false;
      this.setCheckoutLock(false);
      document.dispatchEvent(new CustomEvent('cart:gift-sync-end'));
    }
  }

  /**
   * Fetches fresh section HTML and parses the gift config from it.
   * This ensures we get Liquid-evaluated trigger states, not stale ones.
   */
  async fetchFreshGiftConfig() {
    try {
      const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('#CartDrawer-GiftConfig');

      if (!el) {
        CartLogger.debug('No gift config in fresh section HTML');
        return null;
      }

      const config = JSON.parse(el.textContent);
      CartLogger.debug('Fresh gift config:', JSON.stringify(config));
      return config;
    } catch (err) {
      CartLogger.error('Failed to fetch fresh gift config:', err);
      return null;
    }
  }

  /**
   * Compares gift rules with actual cart and returns required actions.
   * Pure function — no side effects.
   *
   * @param {Array} config - Fresh Liquid-evaluated gift rules
   * @param {Object} cart - Cart state from /cart.js
   * @returns {Array} Actions to execute
   */
  calculateGiftActions(config, cart) {
    const actions = [];
    const cartByVariant = {};
    cart.items.forEach(item => {
      cartByVariant[item.variant_id] = item;
    });

    for (const rule of config) {
      const vid = parseInt(rule.giftVariantId);
      if (!vid) continue;

      const maxQty = parseInt(rule.maxQty) || 1;
      const minCents = parseInt(rule.minCartCents) || 0;
      const hasTag = rule.triggerTag && rule.triggerTag.length > 0;
      const hasAmount = minCents > 0;

      /* triggerActive comes from FRESH Liquid — it already evaluated tags */
      const tagMet = hasTag ? rule.triggerActive === true || rule.triggerActive === 'true' : false;
      const amountMet = hasAmount ? cart.total_price >= minCents : false;

      let shouldBeActive = false;
      if (hasTag && hasAmount) shouldBeActive = tagMet && amountMet;
      else if (hasTag) shouldBeActive = tagMet;
      else if (hasAmount) shouldBeActive = amountMet;

      const item = cartByVariant[vid];
      const inCart = !!item;

      CartLogger.debug(`Rule: tag="${rule.triggerTag}" amount=${minCents}`, {
        tagMet, amountMet, shouldBeActive, inCart,
        currentQty: item?.quantity || 0
      });

      /* Determine action */
      if (shouldBeActive && !inCart) {
        actions.push({ type: 'add', variantId: vid, quantity: 1 });
      } else if (!shouldBeActive && inCart) {
        actions.push({ type: 'remove', variantId: vid, lineKey: item.key });
      } else if (shouldBeActive && inCart && item.quantity > maxQty) {
        actions.push({ type: 'cap', variantId: vid, lineKey: item.key, quantity: maxQty });
      }
    }

    return actions;
  }

setCheckoutLock(locked) {
    if (locked) {
      this.classList.add('cart-drawer--syncing');
      CartLogger.debug('Checkout: LOCKED');
    } else {
      /* Small delay before removing overlay for smooth visual transition */
      setTimeout(() => {
        this.classList.remove('cart-drawer--syncing');
        CartLogger.debug('Checkout: UNLOCKED');
      }, 300);
    }
  }

  /* =========================================
     SECTION: Discount
  ========================================= */
  async applyDiscount(code) {
    if (!code?.trim()) return { success: false };
    CartLogger.info('Applying discount:', code.trim());
    try {
      await fetch(`/discount/${encodeURIComponent(code.trim())}`, { method: 'GET' });
      await this.refreshSections();
      return { success: true };
    } catch (err) {
      CartLogger.error('Discount failed:', err);
      return { success: false, error: err };
    }
  }

  async clearDiscount() {
    CartLogger.info('Clearing discount');
    try {
      await fetch('/discount/clear', { method: 'GET' });
      await this.refreshSections();
    } catch (err) { CartLogger.error('Discount clear failed:', err); }
  }

  /* =========================================
     SECTION: Cart API Helpers
  ========================================= */
  fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' }
    }).then(r => r.json());
  }

  cartPost(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(r => {
      if (!r.ok) throw new Error(`${url}: ${r.status}`);
      return r.json();
    });
  }
}

customElements.define('cart-drawer', CartDrawer);


/* =========================================
   SECTION: CartDrawerItems
   After every quantity update, triggers gift sync.
========================================= */
class CartDrawerItems extends CartItems {
getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  /**
   * CRITICAL: Lock checkout IMMEDIATELY when any quantity change begins.
   * This fires BEFORE the AJAX request, preventing checkout race conditions.
   */
  onChange(event) {
    document.querySelector('cart-drawer')?.setCheckoutLock(true);
    super.onChange(event);
  }

  /**
   * Override: after parent finishes updating, run gift sync.
   * Uses pub/sub event from Dawn's CartItems to know when done.
   */
  connectedCallback() {
    super.connectedCallback?.();

    /* Listen for cart updates to trigger gift sync */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._giftSyncUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'gift-sync') return;
        CartLogger.debug('Cart updated, scheduling gift sync');
        clearTimeout(this._giftSyncTimer);
        this._giftSyncTimer = setTimeout(() => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            drawer.rebindOverlay();
            drawer.runGiftSync();
          }
        }, 800);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this._giftSyncUnsub) this._giftSyncUnsub();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/* =========================================
   SECTION: Discount Form Handler
========================================= */
document.addEventListener('submit', e => {
  const form = e.target.closest('[data-discount-form]');
  if (!form) return;
  e.preventDefault();
  const input = form.querySelector('[data-discount-input]');
  const errEl = form.querySelector('[data-discount-error]');
  if (!input?.value.trim()) {
    if (errEl) { errEl.textContent = 'Please enter a code'; errEl.style.display = 'block'; }
    return;
  }
  if (errEl) errEl.style.display = 'none';
  document.querySelector('cart-drawer')?.applyDiscount(input.value).then(r => {
    if (r?.success) input.value = '';
    else if (errEl) { errEl.textContent = 'Invalid discount code'; errEl.style.display = 'block'; }
  });
});

document.addEventListener('click', e => {
  if (!e.target.closest('[data-discount-clear]')) return;
  e.preventDefault();
  document.querySelector('cart-drawer')?.clearDiscount();
});


/* =========================================
   SECTION: Cart Drawer Tests
   Run: CartTests.runAll() in browser console
========================================= */
window.CartTests = {
  async runAll() {
    console.log('%c=== Cart Drawer Tests ===', 'font-weight:bold;font-size:14px;color:#1a73e8');
    const results = [];
    const assert = (cond, name) => {
      results.push({ pass: cond, name });
      console.log(cond ? '  ✅' : '  ❌', name);
    };

    /* DOM Tests */
    console.group('DOM Structure');
    assert(!!document.querySelector('cart-drawer'), 'cart-drawer element exists');
    assert(!!document.querySelector('#CartDrawer'), 'CartDrawer wrapper exists');
    assert(!!document.querySelector('#CartDrawer-Overlay'), 'Overlay exists');
    assert(!!document.querySelector('.drawer__inner'), 'Inner panel exists');
    assert(!!document.querySelector('.drawer__header'), 'Header exists');
    assert(!!document.querySelector('.drawer__close'), 'Close button exists');
    assert(!!document.querySelector('#CartDrawer-Form'), 'Form exists');
    assert(!!document.querySelector('[data-checkout-btn]'), 'Checkout button exists');
    console.groupEnd();

    /* Component Tests */
    console.group('Web Components');
    assert(!!customElements.get('cart-drawer'), 'cart-drawer registered');
    assert(!!customElements.get('cart-drawer-items'), 'cart-drawer-items registered');
    assert(!!customElements.get('cart-items'), 'cart-items registered');
    assert(!!customElements.get('cart-remove-button'), 'cart-remove-button registered');
    assert(!!customElements.get('cart-note'), 'cart-note registered');

    const drawer = document.querySelector('cart-drawer');
    assert(typeof drawer.open === 'function', 'CartDrawer.open() exists');
    assert(typeof drawer.close === 'function', 'CartDrawer.close() exists');
    assert(typeof drawer.refreshAndOpen === 'function', 'CartDrawer.refreshAndOpen() exists');
    assert(typeof drawer.renderContents === 'function', 'CartDrawer.renderContents() exists');
    assert(typeof drawer.runGiftSync === 'function', 'CartDrawer.runGiftSync() exists');
    assert(typeof drawer.applyDiscount === 'function', 'CartDrawer.applyDiscount() exists');
    assert(typeof drawer.fetchFreshGiftConfig === 'function', 'CartDrawer.fetchFreshGiftConfig() exists');
    assert(typeof drawer.calculateGiftActions === 'function', 'CartDrawer.calculateGiftActions() exists');
    console.groupEnd();

    /* Gift Config Tests */
    console.group('Gift Config');
    const config = await drawer.fetchFreshGiftConfig();
    assert(config === null || Array.isArray(config), 'Gift config is null or array');
    if (config && config.length > 0) {
      config.forEach((rule, i) => {
        assert('triggerActive' in rule, `Rule ${i}: has triggerActive`);
        assert('giftVariantId' in rule, `Rule ${i}: has giftVariantId`);
        assert('maxQty' in rule, `Rule ${i}: has maxQty`);
        assert('minCartCents' in rule, `Rule ${i}: has minCartCents`);
        assert(!isNaN(parseInt(rule.giftVariantId)), `Rule ${i}: giftVariantId is numeric`);
      });
    } else {
      console.log('  ℹ️ No gift rules configured');
    }
    console.groupEnd();

    /* Cart API Tests */
    console.group('Cart API');
    try {
      const cart = await drawer.fetchCart();
      assert(typeof cart === 'object', 'fetchCart returns object');
      assert('items' in cart, 'Cart has items');
      assert('total_price' in cart, 'Cart has total_price');
      assert('item_count' in cart, 'Cart has item_count');
      console.log(`  ℹ️ Cart: ${cart.item_count} items, ${(cart.total_price/100).toFixed(2)} ${cart.currency}`);
    } catch (e) {
      assert(false, 'fetchCart works: ' + e.message);
    }
    console.groupEnd();

    /* Gift Action Calculator Tests */
    console.group('Gift Action Calculator');
    const mockCart = { items: [], total_price: 0, item_count: 0 };
    const mockRules = [
      { triggerTag: '', triggerActive: false, giftVariantId: '999', maxQty: 1, minCartCents: 10000 }
    ];

    let actions = drawer.calculateGiftActions(mockRules, mockCart);
    assert(actions.length === 0, 'No action when cart empty and threshold not met');

    const mockCart2 = { items: [], total_price: 15000, item_count: 2 };
    actions = drawer.calculateGiftActions(mockRules, mockCart2);
    assert(actions.length === 1 && actions[0].type === 'add', 'Add action when amount threshold met');

    const mockCart3 = {
      items: [{ variant_id: 999, key: 'abc', quantity: 1 }],
      total_price: 5000, item_count: 1
    };
    actions = drawer.calculateGiftActions(mockRules, mockCart3);
    assert(actions.length === 1 && actions[0].type === 'remove', 'Remove action when threshold no longer met');

    const mockRulesTag = [
      { triggerTag: 'VIP', triggerActive: true, giftVariantId: '888', maxQty: 1, minCartCents: 0 }
    ];
    actions = drawer.calculateGiftActions(mockRulesTag, { items: [], total_price: 0, item_count: 0 });
    assert(actions.length === 1 && actions[0].type === 'add', 'Add action when tag trigger is active');

    const mockRulesTag2 = [
      { triggerTag: 'VIP', triggerActive: false, giftVariantId: '888', maxQty: 1, minCartCents: 0 }
    ];
    const mockCart4 = { items: [{ variant_id: 888, key: 'def', quantity: 1 }], total_price: 0, item_count: 1 };
    actions = drawer.calculateGiftActions(mockRulesTag2, mockCart4);
    assert(actions.length === 1 && actions[0].type === 'remove', 'Remove when tag trigger deactivated');

    const mockRulesCap = [
      { triggerTag: 'VIP', triggerActive: true, giftVariantId: '777', maxQty: 1, minCartCents: 0 }
    ];
    const mockCart5 = { items: [{ variant_id: 777, key: 'ghi', quantity: 5 }], total_price: 0, item_count: 5 };
    actions = drawer.calculateGiftActions(mockRulesCap, mockCart5);
    assert(actions.length === 1 && actions[0].type === 'cap', 'Cap when qty exceeds max');
    console.groupEnd();

    /* Open/Close Tests */
    console.group('Open / Close');
    drawer.open();
    await new Promise(r => setTimeout(r, 400));
    assert(drawer.classList.contains('active'), 'Drawer has .active after open');
    assert(document.body.classList.contains('overflow-hidden'), 'Body overflow hidden');

    drawer.close();
    await new Promise(r => setTimeout(r, 400));
    assert(!drawer.classList.contains('active'), 'Drawer loses .active after close');
    assert(!document.body.classList.contains('overflow-hidden'), 'Body overflow restored');
    console.groupEnd();

    /* Summary */
    const passed = results.filter(r => r.pass).length;
    const failed = results.filter(r => !r.pass).length;
    const color = failed === 0 ? '#2e7d32' : '#cc0000';
    console.log(`\n%c${passed}/${results.length} passed, ${failed} failed`,
      `font-weight:bold;font-size:14px;color:${color}`);

    if (failed > 0) {
      console.log('%cFailed:', 'color:#cc0000;font-weight:bold');
      results.filter(r => !r.pass).forEach(r => console.log('  ❌', r.name));
    }

    return { passed, failed, total: results.length };
  },

  /* Debug utilities */
  debug: {
    async state() {
      const cart = await fetch('/cart.js').then(r => r.json());
      console.table(cart.items.map(i => ({
        title: i.title, variant_id: i.variant_id, qty: i.quantity,
        price: (i.price/100).toFixed(2), key: i.key.substring(0, 20)
      })));
      console.log('Total:', (cart.total_price/100).toFixed(2), cart.currency);
    },

    async gifts() {
      const drawer = document.querySelector('cart-drawer');
      const config = await drawer.fetchFreshGiftConfig();
      const cart = await drawer.fetchCart();
      console.log('Fresh config:', config);
      console.log('Actions needed:', drawer.calculateGiftActions(config || [], cart));
    },

    verbose() { window.CART_DEBUG = true; console.log('Verbose logging ON'); },
    quiet() { window.CART_DEBUG = false; console.log('Verbose logging OFF'); }
  }
};